import React, { useState, useEffect, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import '../Css/Performance.css';
import Logger from './Logger';
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const Performance = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Initialize Logger
  const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`/api/driver/p/getfeedback`);

        if (!response.ok) {
          logger.log('Failed to fetch performance data', 'ERROR');
          setError('Failed to fetch data');
          return;
        }

        const data = await response.json();
        setData(data);
        setLoading(false);
      } catch (error) {
        logger.log(`Error fetching data: ${error.message}`, 'ERROR');
        setError('Error fetching data');
      }
    };

    fetchData();
  }, [logger]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredData = data.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Aggregate data for each unique name
  const aggregatedData = filteredData.reduce((accumulator, currentValue) => {
    const existingEntry = accumulator.find(item => item.name === currentValue.name);
    if (existingEntry) {
      existingEntry.on_time += parseInt(currentValue.on_time) || 0;
      existingEntry.late_time += parseInt(currentValue.late_time) || 0;
      existingEntry.total += parseInt(currentValue.total) || 0;
    } else {
      accumulator.push({
        ...currentValue,
        on_time: parseInt(currentValue.on_time) || 0,
        late_time: parseInt(currentValue.late_time) || 0,
        total: parseInt(currentValue.total) || 0
      });
    }
    return accumulator;
  }, []);

  const deliveryCounts = {};
  aggregatedData.forEach(driver => {
    const name = driver.name;
    const totalDeliveries = driver.total || 0;
    deliveryCounts[name] = totalDeliveries;
  });

  const chartData = {
    labels: Object.keys(deliveryCounts),
    datasets: [
      {
        label: 'Total Deliveries',
        data: Object.values(deliveryCounts),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
        hoverRadius: 10,
      },
    ],
  };

  return (
    <div>
      <div className='buxx'>
        <input type="text" className="searc" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} autocomplete="off" />
      </div>
      <Bar
        data={chartData}
        options={{
          plugins: {
            tooltip: {
              enabled: true,
            },
            hover: {
              mode: 'nearest',
              intersect: true,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Total Deliveries',
              },
            },
          },
        }}
      />
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Phone Number</th>
            <th>On Time Delivery</th>
            <th>Late Delivery</th>
            <th>Total Delivery</th>
            <th>Customer Feedback</th>
            <th>Average Time</th>
          </tr>
        </thead>
        <tbody>
          {aggregatedData.map((item, index) => (
            item.name ? (
              <tr key={index}>
                <td>{item.name}</td>
                <td>{item.phone}</td>
                <td>{isNaN(item.on_time) ? 0 : item.on_time}</td>
                <td>{isNaN(item.late_time) ? 0 : item.late_time}</td>
                <td>{isNaN(item.total) ? 0 : item.total}</td>
                <td>{item.feedback}</td>
                <td>{item.time}</td>
              </tr>
            ) : null
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Performance;
